/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { LISTING_STATUS } from './constants/listingStatus';
import { updateListingVariantInState } from './helpers/channelHelpers';
import {
  findListingEntity,
  getListingProductIdFromState,
  listListingVariantsForChannel,
} from './helpers/entityReadHelpers';
import { upsertListing, upsertListingVariant } from './helpers/entityAdapter';

export const revertVariantDraftToPublished = createAsyncThunk(
  'products/revertVariantDraftToPublished',
  async ({ masterProductId, channelId, listingVariantId }, { getState, extra: { sdk } }) => {
    const listingProductId = getListingProductIdFromState(getState().products, masterProductId, channelId);

    if (!listingProductId) throw new Error('Listing not found');

    const { data: updatedVariant } = await sdk.products.updateVariant(
      listingProductId,
      listingVariantId,
      { listingStatus: LISTING_STATUS.PUBLISHED },
    );

    return { masterProductId, channelId, updatedVariant };
  },
);

export const reducer = (builder) => {
  builder
    .addCase(revertVariantDraftToPublished.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(revertVariantDraftToPublished.fulfilled, (state, { payload }) => {
      const { masterProductId, channelId, updatedVariant } = payload;
      state.isBusy = false;

      updateListingVariantInState(state, { masterProductId, channelId, data: updatedVariant });

      const existingListing = findListingEntity(state, { masterProductId, channelId });
      if (!existingListing) return;

      if (updatedVariant?._id) {
        upsertListingVariant(state, {
          ...(state.entities?.listingVariants?.[updatedVariant._id] || {}),
          ...updatedVariant,
        });
      }

      if (existingListing.live) {
        const liveVariants = listListingVariantsForChannel(state, masterProductId, channelId)
          .filter((entry) => entry?.live)
          .map((entry) => ({
            ...entry.live,
            _id: entry._id,
            masterVariantId: entry.masterVariantId,
            title: entry.live.title || entry.title,
          }));

        upsertListing(state, {
          ...existingListing,
          masterProductId,
          live: {
            ...existingListing.live,
            variants: liveVariants,
          },
        });
      }
    })
    .addCase(revertVariantDraftToPublished.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot revert variant draft to published';
    });
};
